import Joi from 'joi'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'
import { useForm, useFieldArray } from 'react-hook-form'

import Btn from '../../../../../UI/Btn'
import BtnAdd from '../../../../../UI/BtnAdd'
import TextField from '../../../../../UI/TextField'
import TextAreaField from '../../../../../UI/TextAreaField'
import TextFieldNumber from '../../../../../UI/TextFieldNumber'

import http from '../../../../../shared/http'
import { TApi } from '../../../../../shared/const'
import { isReleaseActive, releaseHint } from '../../../../../shared/revisionRelease'
import { schemaOptions } from '../../../../../shared/i18n'

import styles from './riskAnalysis.module.scss'

/*
 * Markt- und Risikoanalyse.
 *
 * Sie war bis 08.08.2026 ein eigener Menuepunkt neben dem Unternehmensprofil,
 * obwohl sie dasselbe beschreibt: das eigene Unternehmen. Im Firmenprofil der
 * Administration steht sie seit jeher als Abschnitt im Profil — hier jetzt
 * ebenso, mit denselben drei Teilen als Eintraege im senkrechten Untermenue.
 *
 * Die drei Teile sind EIN Formular mit EINEM Speichern-Knopf: Geprueft wird
 * immer alles. Deshalb bleiben alle drei im Seiteninhalt und werden nur
 * ausgeblendet, und deshalb steht der Knopf oben statt am Ende eines Teils.
 */

export const RISK_TABS = [
	{ key: 'market', label: 'Market Description' },
	{ key: 'forecast', label: 'Sales forecast' },
	{ key: 'risk', label: 'Risk and strategy' }
]

export const RISK_TAB_KEYS = RISK_TABS.map(tab => tab.key)

// Welches Feld gehoert zu welchem Teil — fuer die Ampel und dafuer, beim
// Speichern zum ersten Teil zu springen, in dem etwas fehlt.
const TAB_OF_FIELD = {
	targetGroup: 'market',
	productUsage: 'market',
	customer: 'market',
	salesForecast: 'forecast',
	riskStrategy: 'risk'
}

/**
 * Ampel je Teil — gruen, sobald er so gespeichert ist, dass er die Vorgaben
 * erfuellt (dieselben Mindestlaengen, gegen die beim Speichern geprueft wird).
 * Sie haengt am gespeicherten Stand, nicht am Tippen.
 */
export const riskTabStatus = saved => ({
	market: !!(
		saved?.targetGroup?.length >= 200 &&
		saved?.productUsage?.length >= 200 &&
		saved?.customer?.length >= 200
	),
	forecast: !!saved?.salesForecast?.some(item => item?.product && item?.price && item?.expectedSales),
	risk: (saved?.riskStrategy || []).filter(item => item?.risk && item?.strategy?.length >= 50).length >= 3
})

const schema = Joi.object({
	totalTurnover: Joi.number().allow(null),
	targetGroup: Joi.string().min(200).max(10000).required().messages({
		'string.empty': `is not allowed to be empty`,
		'string.min': `must be greater than 200 characters`,
		'string.max': `must be less than 10000 characters`
	}),
	productUsage: Joi.string().min(200).max(10000).required().messages({
		'string.empty': `is not allowed to be empty`,
		'string.min': `must be greater than 200 characters`,
		'string.max': `must be less than 10000 characters`
	}),
	customer: Joi.string().min(200).max(10000).required().messages({
		'string.empty': `is not allowed to be empty`,
		'string.min': `must be greater than 200 characters`,
		'string.max': `must be less than 10000 characters`
	}),
	riskStrategy: Joi.array()
		.items(
			Joi.object({
				risk: Joi.string().min(2).max(1000).required().messages({
					'string.empty': `is not allowed to be empty`
				}),
				strategy: Joi.string().min(50).max(10000).required().messages({
					'string.empty': `is not allowed to be empty`
				})
			})
		)
		.min(3),
	salesForecast: Joi.array().items(
		Joi.object({
			product: Joi.string().messages({
				'string.empty': `is not allowed to be empty`
			}),
			price: Joi.number().messages({
				'number.base': `must be a number`
			}),
			expectedSales: Joi.number().messages({
				'number.base': `must be a number`
			}),
			expectedTurnover: Joi.number().messages({
				'number.base': `must be a number`
			})
		})
	)
})

function RiskAnalysis(props) {
	const { t } = useTranslation()
	const { activeTab, onNavigate, onStatusChange } = props

	const {
		register,
		handleSubmit,
		control,
		trigger,
		reset,
		watch,
		formState: { errors }
	} = useForm({
		defaultValues: {
			salesForecast: [{ product: '', price: '', expectedSales: '' }],
			riskStrategy: [
				{ risk: '', strategy: '' },
				{ risk: '', strategy: '' },
				{ risk: '', strategy: '' }
			]
		},
		resolver: joiResolver(schema, {
			errors: {
				labels: false,
				language: localStorage.getItem('i18nextLng')
			},
			...schemaOptions
		})
	})

	useEffect(() => {
		if (Object.keys(errors).length) trigger()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localStorage.getItem('i18nextLng')])

	const {
		fields: salesForecast,
		append,
		remove
	} = useFieldArray({
		reset,
		control,
		name: 'salesForecast'
	})

	const {
		fields: riskStrategy,
		append: riskAppend,
		remove: riskRemove
	} = useFieldArray({
		reset,
		control,
		name: 'riskStrategy'
	})
	const salesForecastWatch = watch('salesForecast')

	const salesFieldsWithTurnover = salesForecastWatch
		.map(item => ({
			...item,
			expectedTurnover: item.price * item.expectedSales
		}))
		.reduce((accumulator, currentValue) => accumulator + currentValue.expectedTurnover, 0)

	const [isLoading, setLoading] = useState(false)
	const [loadFailed, setLoadFailed] = useState(false)
	// Stand, wie er gespeichert ist — daran haengt die Ampel am Abschnitt.
	const [saved, setSaved] = useState(null)

	// Die Ampeln stehen im Untermenue der Seite daneben, also gehoert der Stand
	// dorthin gemeldet — sonst muesste man jeden Abschnitt oeffnen, um zu sehen,
	// wo noch etwas fehlt.
	useEffect(() => {
		onStatusChange?.(riskTabStatus(saved))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saved])

	const onSubmit = data => {
		setLoading(true)
		const { totalTurnover, ...form } = data

		const expectTurn = data.salesForecast.map(item => ({
			...item,
			expectedTurnover: item.price * item.expectedSales
		}))
		const finalData = {
			...form,
			salesForecast: expectTurn,
			totalAmoutOfSales: salesFieldsWithTurnover
		}

		http.put(TApi.STUDENT_COMPANY_INSURANCE, finalData)
			.then(() => {
				toast.success(t('Risk analysis form successfully updated'))
				setSaved(finalData)
			})
			.finally(() => setLoading(false))
	}

	/*
	 * Der Speichern-Knopf prueft immer das ganze Formular. Fehlt etwas in einem
	 * Teil, den man gerade nicht sieht, waere sonst nicht zu erkennen, warum
	 * nichts passiert — also dorthin wechseln.
	 */
	const onInvalid = formErrors => {
		const firstTab = RISK_TABS.find(tab => Object.keys(formErrors).some(field => TAB_OF_FIELD[field] === tab.key))
		if (firstTab) onNavigate?.(firstTab.key)
	}

	/*
	 * Scheitert die Abfrage, zeigt der Abschnitt nicht das leere Formular: Das
	 * sah aus wie eine Analyse, die noch niemand ausgefuellt hat — und ein
	 * Speichern haette den vorhandenen Stand ueberschrieben.
	 */
	const fetchInsurance = () => {
		http.get(TApi.STUDENT_COMPANY_INSURANCE)
			.then(({ data }) => {
				setLoadFailed(false)
				if (!data) return

				setSaved(data)
				reset({
					productUsage: data.productUsage || '',
					targetGroup: data.targetGroup || '',
					customer: data.customer || '',
					riskStrategy: data.riskStrategy?.length
						? data.riskStrategy
						: [
								{ risk: '', strategy: '' },
								{ risk: '', strategy: '' },
								{ risk: '', strategy: '' }
						  ],
					salesForecast: data.salesForecast?.length
						? data.salesForecast
						: [{ product: '', price: '', expectedSales: '' }]
				})
			})
			.catch(() => setLoadFailed(true))
	}

	useEffect(() => {
		fetchInsurance()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	/*
	 * Gesperrt nach dem Einreichen und nach der Aktivierung — seit #230 auch im
	 * Server. Eine Freigabe der Verwaltung öffnet die Analyse für ihre Dauer.
	 */
	const releaseOpen = isReleaseActive(props.revisionRelease)
	const locked = !releaseOpen && (props.isActivatedCompany || props.isNeedReview)
	const isDisabledFields = isLoading || locked
	const canSave = !locked && !props.isTeacherStudentView
	const hint = releaseHint(t, props.revisionRelease)

	// Sichtbar ist die Analyse nur, solange einer ihrer drei Teile gewaehlt ist.
	// Sie bleibt aber im Seiteninhalt: Ausgehaengt waeren getippte, noch nicht
	// gespeicherte Eingaben beim Wechsel in die Stammdaten weg.
	const isVisible = RISK_TAB_KEYS.includes(activeTab)

	if (loadFailed) {
		return (
			<div className={clsx(styles.loadError, !isVisible && styles.formHidden)}>
				<p>{t('The risk analysis could not be loaded.')}</p>
				<Btn icon="arrow" iconSize={{ w: 10, h: 10 }} iconColor="#fff" onClick={fetchInsurance}>
					{t('Try again')}
				</Btn>
			</div>
		)
	}

	return (
		<form
			onSubmit={handleSubmit(onSubmit, onInvalid)}
			className={clsx(styles.form, !isVisible && styles.formHidden)}
		>
			{/* Ein Formular, ein Speichern-Knopf: Er gilt fuer alle drei Teile
			    und steht deshalb ueber ihnen statt im letzten Abschnitt. */}
			{hint && (
				<p className={styles.releaseHint} role="status">
					{hint}
				</p>
			)}
			{canSave && (
				<div className={styles.actionBar}>
					<Btn
						className={styles.actionBtn}
						loading={isLoading}
						type="submit"
						icon="arrow"
						iconSize={{ w: 10, h: 10 }}
						iconColor="#fff"
					>
						{t('save')}
					</Btn>
				</div>
			)}

			<div className={clsx(styles.part, activeTab !== 'market' && styles.partHidden)}>
				<div className={styles.content}>
					<div className={styles.flexWrapp}>
						<TextAreaField
							disabled={isDisabledFields}
							classNameLabel={styles.textArea}
							height={99}
							label={t('Target group(s)')}
							placeholder={t('Target group(s)')}
							error={errors.targetGroup}
							register={register('targetGroup')}
						/>
						<div className={styles.noteRight}>
							<h3 className={styles.noteTitle}>{t('To market description')}</h3>
							{t(
								'Describe your target group(s) as precisely as possible with their specific characteristics, such as age group, life situation, interests, whereabouts, etc.'
							)}
						</div>
					</div>

					<div className={styles.flexWrapp}>
						<TextAreaField
							disabled={isDisabledFields}
							classNameLabel={styles.textArea}
							height={99}
							label={t('Product usage')}
							placeholder={t('Add usage...')}
							error={errors.productUsage}
							register={register('productUsage')}
						/>

						<div className={styles.noteRight}>
							<h3 className={styles.noteTitle}>{t('To product benefit')}</h3>
							{t(
								'What added value does your offer bring to the target group, what needs does it serve? For what benefit does the customer pay the purchase price?'
							)}
						</div>
					</div>

					<div className={styles.flexWrapp}>
						<TextAreaField
							disabled={isDisabledFields}
							classNameLabel={styles.textArea}
							height={99}
							label={t('Customer contact, sales strategy')}
							placeholder={t('Add contact & Strategy')}
							error={errors.customer}
							register={register('customer')}
						/>
						<div className={styles.noteRight}>
							<h3 className={styles.noteTitle}>{t('Customer contact, sales strategy')}</h3>
							{t(
								'Where is/are your target group(s) to be found? How do you promote your offer to the respective target group? Which communication channels do you use?'
							)}
						</div>
					</div>
				</div>
			</div>

			<div className={clsx(styles.part, activeTab !== 'forecast' && styles.partHidden)}>
				<div className={styles.content}>
					<p className={styles.note}>
						{t(
							'Enter your expected sales figures for your up to 5 most important offers and their respective prices. This will give you a forecast of your sales for the entire project year.'
						)}
					</p>
					{salesForecast.map((item, index) => {
						return (
							<div key={item.id} style={{ width: '100%' }}>
								<div className={styles.paar} style={{ marginBottom: 40 }}>
									<TextField
										disabled={isDisabledFields}
										className={styles.field}
										label={t('Product / Service')}
										placeholder={t('Product name')}
										error={errors.salesForecast && errors.salesForecast[index]?.product}
										register={register(`salesForecast.${index}.product`)}
									/>
									<TextFieldNumber
										disabled={isDisabledFields}
										className={styles.field}
										label={t('Price (€) / unit')}
										placeholder="€ 00.000,00"
										error={errors.salesForecast && errors.salesForecast[index]?.price}
										register={register(`salesForecast.${index}.price`)}
										control={control}
										name={`salesForecast.${index}.price`}
									/>
								</div>
								<div className={styles.paar} style={{ marginBottom: 40 }}>
									<TextFieldNumber
										disabled={isDisabledFields}
										className={styles.field}
										name={`salesForecast.${index}.expectedSales`}
										control={control}
										label={t('Expected sales (number)')}
										placeholder="00.000"
										noDecimal
										error={errors.salesForecast && errors.salesForecast[index]?.expectedSales}
									/>
									<TextFieldNumber
										className={styles.field}
										readOnly
										disabled={isDisabledFields}
										value={
											(salesForecastWatch[index].price || 0) *
											(salesForecastWatch[index].expectedSales || 0)
										}
										label={t('Expected turnover (€)')}
										placeholder="€ 00.000,00"
										control={control}
										name={`salesForecast.${index}.expectedTurnover`}
									/>
								</div>
								{index === 0 ? (
									''
								) : (
									<Btn
										disabled={isDisabledFields}
										className={styles.deleteBtn}
										theme="white"
										icon="delete"
										iconColor="#000"
										onClick={() => remove(index)}
									/>
								)}
							</div>
						)
					})}

					<TextFieldNumber
						readOnly
						className={styles.field}
						value={salesFieldsWithTurnover}
						disabled={isDisabledFields}
						mb={36}
						label={t('The total amount of estimated annual sales is €.')}
						placeholder="€ 00.000,00"
						name={'totalTurnover'}
						control={control}
					/>

					{!props.isTeacherStudentView && (
						<div className={styles.btnAdd}>
							<BtnAdd
								type="button"
								disabled={isDisabledFields}
								onClick={() => append({ product: '', price: '', expectedSales: '' })}
							/>
							<h1 className={styles.btnTitle}>{t('Add new forecast')}</h1>
						</div>
					)}
				</div>
			</div>

			<div className={clsx(styles.part, activeTab !== 'risk' && styles.partHidden)}>
				<div className={styles.content}>
					<p className={styles.note}>
						{t(
							'Enter here possible risks affecting your company and describe a strategy for dealing with the risk in each case. Risks can arise, for example, in connection with your production, with the target group, with suppliers or with the competition.'
						)}
					</p>
					{riskStrategy.map((item, index) => {
						return (
							<div key={item.id} style={{ width: '100%' }}>
								<TextAreaField
									disabled={isDisabledFields}
									classNameLabel={styles.textArea}
									mb={36}
									height={48}
									label={t('Risk')}
									placeholder={t('Risk')}
									error={errors.riskStrategy && errors.riskStrategy[index]?.risk}
									register={register(`riskStrategy.${index}.risk`)}
								/>

								<TextAreaField
									disabled={isDisabledFields}
									classNameLabel={styles.textArea}
									mb={36}
									height={99}
									label={t('Avoidance strategy')}
									placeholder={t('Add avoidance strategy')}
									error={errors.riskStrategy && errors.riskStrategy[index]?.strategy}
									register={register(`riskStrategy.${index}.strategy`)}
								/>
								{index !== 0 && index !== 1 && index !== 2 && (
									<Btn
										disabled={isLoading || locked}
										className={styles.deleteBtn}
										theme="white"
										icon="delete"
										iconColor="#000"
										onClick={() => riskRemove(index)}
									/>
								)}
							</div>
						)
					})}
					{!props.isTeacherStudentView && (
						<div className={styles.btnAdd}>
							<BtnAdd
								type="button"
								disabled={locked}
								onClick={() => riskAppend({ risk: '', strategy: '' })}
							/>
							<h1 className={styles.btnTitle}>{t('Add new Risk')}</h1>
						</div>
					)}
				</div>
			</div>
		</form>
	)
}

RiskAnalysis.propTypes = {
	activeTab: PropTypes.string.isRequired,
	onNavigate: PropTypes.func,
	onStatusChange: PropTypes.func,
	isActivatedCompany: PropTypes.bool.isRequired,
	isNeedReview: PropTypes.bool.isRequired,
	isTeacherStudentView: PropTypes.bool.isRequired,
	// Freigabe zur Überarbeitung (#230), aus dem Profil der eigenen Company.
	revisionRelease: PropTypes.object
}

const mapStateToProps = state => ({
	isActivatedCompany: !!state.user.profile.EmploymentCompany?.isActive,
	isNeedReview: !!state.user.profile.EmploymentCompany?.isNeedReview,
	isTeacherStudentView: state.user.isTeacherStudentView
})

export default connect(mapStateToProps)(RiskAnalysis)
